import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { TextArea } from "@design-system/TextArea/TextArea";
import styles from "./TaskBoardChips.module.scss";

export const LINE_BREAKS = /\s*\n\s*/g;

/**
 * Whether a title field's blur came from a tab or app switch: the field
 * stays the active element and gets focus back on return, so the edit goes on.
 */
export const isWindowSwitchBlur = (field: HTMLElement | null) =>
    document.activeElement === field;

interface TaskTitleEditorProps {
    /** The title when the rename started; the field starts from it. */
    initialTitle: string;
    onRename: (title: string) => void;
    /** Ends the edit: the card shows its title again. */
    onEnd: () => void;
    /** Takes focus back after Enter or Escape. */
    onReturnFocus: () => void;
    placeholder?: string;
}

/**
 * The card title as a field, current title selected. Enter or leaving the
 * field for another part of the page saves the trimmed title (empty or
 * unchanged saves nothing); switching tabs or apps keeps the edit; Escape
 * cancels. Enter and Escape hand focus back to the card, and stop there so
 * the board doesn't clear its selection. The title stays one line of text:
 * a pasted line break becomes a space.
 */
export const TaskTitleEditor = ({
    initialTitle,
    onRename,
    onEnd,
    onReturnFocus,
    placeholder,
}: Readonly<TaskTitleEditorProps>) => {
    const [draft, setDraft] = useState(initialTitle);
    const fieldRef = useRef<HTMLTextAreaElement>(null);
    const isCanceledRef = useRef(false);
    useEffect(() => {
        fieldRef.current?.focus({ preventScroll: true });
        fieldRef.current?.select();
    }, []);

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (
            (event.key === "Enter" || event.key === "Escape") &&
            !event.nativeEvent.isComposing
        ) {
            event.preventDefault();
            isCanceledRef.current = event.key === "Escape";
            onReturnFocus();
        }
    };
    const handleBlur = () => {
        if (isWindowSwitchBlur(fieldRef.current)) {
            return;
        }
        const nextTitle = draft.trim();
        if (
            !isCanceledRef.current &&
            nextTitle !== "" &&
            nextTitle !== initialTitle
        ) {
            onRename(nextTitle);
        }
        onEnd();
    };

    return (
        <TextArea
            ref={fieldRef}
            autoGrow
            minRows={1}
            aria-label="Task title"
            placeholder={placeholder}
            value={draft}
            onValueChange={(value) => setDraft(value.replace(LINE_BREAKS, " "))}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className={styles.titleField}
        />
    );
};
